import { lazy, Suspense, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import {
  Company,
  CompanyFilterKey,
  CompanyFilters,
  CompanySort,
  CompanyView,
  DEFAULT_COMPANY_VIEW,
  NO_COMPANY_FILTERS as NO_FILTERS,
  loadCompanies,
  searchCompanies,
  sortCompanies,
} from '../companies';
import {
  companyViewFromParams,
  companyViewToParams,
  pruneCompanyFilters,
  pageFromParams,
  withPage,
} from '../filterParams';
import { Pagination, rangeLabel } from './Pagination';
import { FilterSelect } from './FilterSelect';
import { FiltersModal, FilterSection } from './FiltersModal';
import { FiltersDisclosure } from './FiltersDisclosure';
import { NOT_SPECIFIED } from '../format';
import { prettyLabel, locationLabel } from '../labels';
import { MANUAL_REVIEW_NOTE, SPONSOR_NOTE, SPONSOR_REGISTER_URL } from '../references';
import { useIsMobile } from '../useMediaQuery';
import { ActiveFilters, ActiveChip } from './ActiveFilters';
import { outboundHref } from '../outbound';
import { pathFor } from '../routes';
// Leaflet and its stylesheet are a big chunk of the site's weight, and only this page's map
// view needs them - split out so they download when someone actually asks for a map, not on
// every visit to the job board.
const CompanyMap = lazy(() => import('./CompanyMap').then((m) => ({ default: m.CompanyMap })));

type View = 'cards' | 'split' | 'map';

const VIEWS: { key: View; label: string }[] = [
  { key: 'cards', label: 'Cards' },
  { key: 'split', label: 'Split' },
  { key: 'map', label: 'Map' },
];

const SORTS: { value: CompanySort; label: string }[] = [
  { value: 'openings', label: 'Number of roles open' },
  { value: 'name', label: 'A to Z' },
];

/** A hand-checked column as a filter value. */
const answerOf = (value: boolean | undefined) =>
  value === true ? 'yes' : value === false ? 'no' : '';

/** The three answers the two hand-checked columns can hold. */
const answerLabel = (value: string) =>
  value === 'yes' ? 'Yes' : value === 'no' ? 'No' : 'Not checked yet';

/** Every filter on this page, defined once. */
const FIELDS: {
  key: CompanyFilterKey;
  label: string;
  pick: (c: Company) => string[];
  format?: (value: string) => string;
  /** Fixed options, where they aren't read off the data. */
  values?: string[];
  /** Shown on an "i" in the open panel, beside the count. */
  tooltip?: string;
  /** A source the tooltip points to, as a clickable line inside the bubble. */
  tooltipLink?: { label: string; href: string };
  /** Given the accent, because they are what this audience came for. */
  accent?: boolean;
}[] = [
  { key: 'hqLocations', label: 'HQ location', pick: (c) => [c.location], format: locationLabel },
  { key: 'industries', label: 'Industry', pick: (c) => c.industries, format: prettyLabel },
  { key: 'companyTypes', label: 'Model & tech', pick: (c) => c.types, format: prettyLabel },
  { key: 'growthStages', label: 'Stage', pick: (c) => [c.growthStage], format: prettyLabel },
  {
    key: 'openRoles',
    label: 'Open roles',
    // Whether, not how many - and off "Board roles" (roles actually listed on our board),
    // not "Job openings" (Dealroom's own careers-page count, which doesn't shrink as roles
    // age off the board and includes roles never matched onto it at all).
    pick: (c) => [c.boardRoles > 0 ? 'yes' : 'no'],
    values: ['yes', 'no'],
    format: (v) => (v === 'yes' ? 'Has open roles' : 'None listed'),
  },
  {
    key: 'sponsor',
    label: 'Accredited sponsor',
    pick: (c) => [answerOf(c.accreditedSponsor)],
    format: answerLabel,
    tooltip: SPONSOR_NOTE,
    tooltipLink: { label: 'Home Affairs sponsor register (PDF)', href: SPONSOR_REGISTER_URL },
    accent: true,
  },
  {
    key: 'students',
    label: 'Hires international students and graduates',
    pick: (c) => [answerOf(c.hiresInternationalStudents)],
    format: answerLabel,
    tooltip: MANUAL_REVIEW_NOTE,
    accent: true,
  },
];

/** Filters that also sit on the bar; the "All filters" dialog holds every one of them. */
const QUICK_KEYS: CompanyFilterKey[] = ['sponsor', 'students'];

/** The dialog's sections - the same headings the job board uses. */
const MODAL_GROUPS: { title: string; keys: CompanyFilterKey[] }[] = [
  { title: 'Visa and sponsorship', keys: ['students', 'sponsor'] },
  { title: 'Where', keys: ['hqLocations'] },
  { title: 'The company', keys: ['industries', 'companyTypes', 'growthStages', 'openRoles'] },
];

const BY_KEY = new Map(FIELDS.map((f) => [f.key, f]));

const base = process.env.PUBLIC_URL || '';

/** The filters the address asks for on arrival - a shared or bookmarked view. */
const viewFromUrl = (): CompanyView =>
  companyViewFromParams(
    new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search),
    DEFAULT_COMPANY_VIEW
  );

/**
 * Cards per page.
 *
 * The list is 15,000 companies; rendering all of them is a browser laid out
 * flat before it can paint anything. The map is exempt - it draws one pin per
 * suburb, not one per company, and a map showing 25 of 15,000 would say
 * something false about where the companies are.
 */
const PAGE_SIZE = 25;

const uniqueSorted = (values: string[]) => Array.from(new Set(values)).sort();

/** Every company on the national list. */
export function Companies() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  // The filters, search and sort all live in the URL query so a narrowed list
  // can be bookmarked or shared; read once here, kept in sync below.
  const start = useRef(viewFromUrl()).current;
  const [query, setQuery] = useState(start.query);
  const [filters, setFilters] = useState<CompanyFilters>(start.filters);
  const [sort, setSort] = useState<CompanySort>(start.sort);
  const [modalOpen, setModalOpen] = useState(false);
  const isMobile = useIsMobile();
  const [page, setPage] = useState(() =>
    pageFromParams(new URLSearchParams(window.location.search))
  );
  const topRef = useRef<HTMLElement>(null);
  const [view, setView] = useState<View>('cards');
  // Which card the pointer is on, so the map can open that suburb alongside it.
  const [hovered, setHovered] = useState<string | null>(null);
  const prunedOnce = useRef(false);

  useEffect(() => {
    loadCompanies()
      .then((data) => {
        setCompanies(data);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, []);

  // Reflect the view into the query string in place - no new history entry per change, and
  // the path (which is always /companies here) is left alone.
  useEffect(() => {
    const qs = withPage(companyViewToParams({ query, filters, sort }), page).toString();
    const search = qs ? `?${qs}` : '';
    if (search === window.location.search) return;
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${search}`);
  }, [query, filters, sort, page]);

  // Back/forward moves between saved views.
  useEffect(() => {
    const onPopState = () => {
      const next = viewFromUrl();
      setQuery(next.query);
      setFilters(next.filters);
      setSort(next.sort);
      setPage(pageFromParams(new URLSearchParams(window.location.search)));
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  /**
   * Every value each filter could take, read off the data - except where the options are
   * fixed.
   */
  const options = useMemo(() => {
    const built = {} as Record<CompanyFilterKey, string[]>;
    for (const field of FIELDS) {
      if (field.values) {
        const present = new Set(companies.flatMap(field.pick));
        built[field.key] = field.values.filter((v) => present.has(v));
        continue;
      }
      const values = uniqueSorted(companies.flatMap(field.pick).filter(Boolean));
      const anyBlank = companies.some((c) => field.pick(c).filter(Boolean).length === 0);
      built[field.key] = anyBlank ? [...values, ''] : values;
    }
    return built;
  }, [companies]);

  // Once the data is in, drop any filter values a shared link asked for that the
  // list can't actually offer, so it never sits there matching nothing.
  useEffect(() => {
    if (status !== 'ready' || prunedOnce.current) return;
    prunedOnce.current = true;
    setFilters((current) => pruneCompanyFilters(current, options));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Values within a filter are OR'd and separate filters are AND'd, matching how the job
  // board's filters behave - one rule to learn, not two.
  const overlaps = (selected: string[], values: string[]) =>
    selected.length === 0 ||
    (values.filter(Boolean).length
      ? values.some((v) => selected.includes(v))
      : selected.includes(''));

  const matches = (c: Company, active: CompanyFilters) =>
    FIELDS.every((field) => overlaps(active[field.key], field.pick(c)));

  // shown/counts each walk the whole company list (counts does it once per
  // filter), too heavy to redo synchronously on every keystroke of the search box without the
  // input itself lagging behind what was typed - see the identical fix, and the explanation,
  // on the job board's own search (App.tsx). Deferring the query lets React keep the input
  // snappy and catch these up a moment later instead.
  const deferredQuery = useDeferredValue(query);

  const shown = useMemo(() => {
    const filtered = searchCompanies(companies, deferredQuery).filter((c) => matches(c, filters));
    return sortCompanies(filtered, sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, deferredQuery, filters, sort]);

  /**
   * How many companies each option would leave, counted with that filter's own selection
   * lifted - otherwise every unpicked value in a filter you have already used reads as
   * zero, because that filter has just excluded them.
   */
  const counts = useMemo(() => {
    const pool = searchCompanies(companies, deferredQuery);
    const built = {} as Record<CompanyFilterKey, Map<string, number>>;

    for (const field of FIELDS) {
      const without = pool.filter((c) => matches(c, { ...filters, [field.key]: [] }));
      const counted = new Map<string, number>();
      without.forEach((c) => {
        const values = field.pick(c).filter(Boolean);
        const keys = values.length ? Array.from(new Set(values)) : [''];
        keys.forEach((v) => counted.set(v, (counted.get(v) ?? 0) + 1));
      });
      built[field.key] = counted;
    }
    return built;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, deferredQuery, filters]);

  // Back to the first page whenever the list underneath changes, so you are
  // never left on page 40 of a set that now has three. Compared by what the view says rather
  // than by identity, so settling a shared link against the data - which rebuilds the filter
  // objects without changing anything - doesn't throw away the page it asked for.
  const viewKey = companyViewToParams({ query, filters, sort }).toString();
  const seenView = useRef(viewKey);
  useEffect(() => {
    if (seenView.current === viewKey) return;
    seenView.current = viewKey;
    setPage(1);
  }, [viewKey]);

  const clearAll = () => setFilters(NO_FILTERS);

  const totalPages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageCompanies = shown.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  /**
   * Turning a page puts you at the top of the new one - the top of the results, not of the
   * page, so the intro and filters you have already scrolled past don't return between pages.
   * The results band carries the scroll-margin that keeps it clear of the sticky header.
   */
  const goToPage = (next: number) => {
    setPage(next);
    topRef.current?.scrollIntoView?.({ block: 'start' });
  };

  /** The address of a page of this same list. */
  const hrefForPage = (n: number) => {
    const params = withPage(companyViewToParams({ query, filters, sort }), n);
    return `${window.location.pathname}${params.toString() ? `?${params}` : ''}`;
  };

  const openings = shown.reduce((total, c) => total + c.openings, 0);
  const label = (value: string, format?: (v: string) => string) =>
    (format ? format(value) : value) || NOT_SPECIFIED;

  // Same chips as the job board: what is narrowing the list, readable at a glance and
  // undoable one at a time.
  const chips: ActiveChip[] = FIELDS.flatMap((field) =>
    filters[field.key].map((value) => ({
      id: `${field.key}:${value}`,
      field: field.label,
      value: label(value, field.format),
      remove: () =>
        setFilters((prev) => ({
          ...prev,
          [field.key]: prev[field.key].filter((v) => v !== value),
        })),
    }))
  );
  const modalCount = MODAL_GROUPS.reduce(
    (total, group) => total + group.keys.reduce((n, key) => n + filters[key].length, 0),
    0
  );

  const selectFor = (key: CompanyFilterKey, overlay: boolean) => {
    const field = BY_KEY.get(key);
    if (!field) return null;
    const el = (
      <FilterSelect
        label={field.label}
        tooltip={field.tooltip}
        tooltipLink={field.tooltipLink}
        searchable={!isMobile}
        overlay={overlay}
        options={options[key].map((v) => ({
          value: v,
          label: label(v, field.format),
          count: counts[key].get(v) ?? 0,
        }))}
        selected={filters[key]}
        onChange={(next) => setFilters((prev) => ({ ...prev, [key]: next }))}
      />
    );
    return field.accent ? (
      <div className={`fselect-sponsor${filters[key].length ? ' is-set' : ''}`}>{el}</div>
    ) : (
      el
    );
  };

  return (
    <>
      <header className="page-intro">
        <h1 id="companies-heading" className="page-intro-title">
          Startups and scaleups founded in Australia
        </h1>
        <p className="page-lead">
          Australian startups and scaleups, and whether they're an accredited visa sponsor or if
          they hire international students and graduates.
        </p>
      </header>

      <div className="filters-region">
        <FiltersDisclosure
          activeCount={chips.length + (query.trim() ? 1 : 0)}
          defaultOpen={!isMobile}
        >
          <div className="filterbar" role="search">
            <div className="filter-row">
              <div className="filter-search">
                <label className="visually-hidden" htmlFor="company-search">
                  Search companies
                </label>
                <img
                  className="search-icon"
                  src={`${base}/icons/magifying-glass.svg`}
                  alt=""
                  aria-hidden="true"
                  width={18}
                  height={18}
                />
                <input
                  id="company-search"
                  type="search"
                  placeholder="Search name or industry"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>

              {QUICK_KEYS.map((key) => (
                <span key={key}>{selectFor(key, false)}</span>
              ))}

              <FilterSelect
                label="Sort"
                multiple={false}
                searchable={false}
                options={SORTS.map((s) => ({ value: s.value, label: s.label }))}
                selected={[sort]}
                onChange={(next) => setSort((next[0] as CompanySort) ?? 'openings')}
              />

              <button
                type="button"
                className={`more-filters${modalCount ? ' is-set' : ''}`}
                onClick={() => setModalOpen(true)}
              >
                All filters
                {modalCount > 0 && <span className="more-filters-count">{modalCount}</span>}
              </button>
            </div>

            <ActiveFilters chips={chips} onClear={clearAll} />

            <FiltersModal
              open={modalOpen}
              onClose={() => setModalOpen(false)}
              onClear={clearAll}
              resultCount={shown.length}
              resultNoun="company"
              resultNounPlural="companies"
            >
              {MODAL_GROUPS.map((group) => (
                <FilterSection title={group.title} key={group.title}>
                  {group.keys.map((key) => (
                    <div className="fmodal-control" key={key}>
                      {selectFor(key, true)}
                    </div>
                  ))}
                </FilterSection>
              ))}
            </FiltersModal>
          </div>
        </FiltersDisclosure>
      </div>

      <section
        className="about-section results-band"
        aria-labelledby="companies-heading"
        ref={topRef}
      >
        {status === 'loading' && <p className="panel-note">Loading companies . . .</p>}
        {status === 'error' && (
          <p className="panel-note" role="alert">
            Sorry, we couldn't load the company list right now. Please try again later.
          </p>
        )}

        {status === 'ready' && (
          <>
            <div className="jobs-head company-head">
              <p className="result-count" aria-live="polite">
                {/* The map draws every company at once; the cards are a page of them. */}
                {view === 'map' || shown.length === 0
                  ? shown.length.toLocaleString('en-AU')
                  : `Showing ${rangeLabel(currentPage, PAGE_SIZE, shown.length)}`}{' '}
                {shown.length === 1 ? 'company' : 'companies'}
                {openings > 0 && ` · ${openings.toLocaleString('en-AU')} open roles`}
              </p>

              <div className="view-toggle" role="group" aria-label="How to show the companies">
                {VIEWS.map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    className={`view-toggle-btn${view === v.key ? ' is-on' : ''}`}
                    aria-pressed={view === v.key}
                    onClick={() => setView(v.key)}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            {shown.length === 0 ? (
              <p className="panel-note">No company matches what you're looking for.</p>
            ) : (
              <div className={`company-views is-${view}`}>
                {view !== 'cards' && (
                  <Suspense
                    fallback={<div className="company-map-canvas is-loading" aria-hidden="true" />}
                  >
                    <CompanyMap companies={shown} highlight={hovered} />
                  </Suspense>
                )}
                {view !== 'map' && (
                  <ul className="company-grid">
                    {pageCompanies.map((company) => (
                      <CompanyCard
                        key={company.name}
                        company={company}
                        onHover={view === 'split' ? setHovered : undefined}
                      />
                    ))}
                  </ul>
                )}
              </div>
            )}

            {view !== 'map' && (
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                label="Company pages"
                hrefFor={hrefForPage}
                onPage={goToPage}
              />
            )}
          </>
        )}
      </section>
    </>
  );
}

/**
 * Card hierarchy, most important first: who they are, what they do, whether they can hire
 * you, what field they're in, and the housekeeping last.
 */
function CompanyCard({
  company,
  onHover,
}: {
  company: Company;
  onHover?: (name: string | null) => void;
}) {
  const href = outboundHref(company.website || company.linkedin, 'startups');
  const meta = [company.segment, company.employees && `${company.employees} people`]
    .filter(Boolean)
    .join(' · ');
  // Long industry lists crowd out everything under them; the rest stay in the title so
  // nothing is hidden outright.
  const industries = company.industries.slice(0, 3);
  const extraIndustries = company.industries.length - industries.length;

  return (
    <li
      className="company-card"
      onMouseEnter={() => onHover?.(company.name)}
      onMouseLeave={() => onHover?.(null)}
      // Focus as well as hover, so the map follows a keyboard too.
      onFocus={() => onHover?.(company.name)}
      onBlur={() => onHover?.(null)}
    >
      <div className="company-card-head">
        {href ? (
          <a
            className="company-card-name"
            href={href}
            target="_blank"
            rel="noopener"
            referrerPolicy="strict-origin-when-cross-origin"
          >
            {company.name}
          </a>
        ) : (
          <span className="company-card-name">{company.name}</span>
        )}
        {company.boardRoles > 0 && (
          <a
            className="company-card-openings"
            href={`${pathFor('jobs')}?company=${encodeURIComponent(company.name)}`}
            // Stops the SPA's document-level link handler from taking over: that handler
            // strips off the query string when it pushes state, which would drop the
            // company filter this link exists to carry. A plain full-page navigation still
            // lands on our own job board, just without that shortcut.
            onClick={(e) => e.stopPropagation()}
          >
            View {company.boardRoles} open {company.boardRoles === 1 ? 'role' : 'roles'}
          </a>
        )}
      </div>

      {company.tagline && <p className="company-card-tagline">{company.tagline}</p>}

      {(company.accreditedSponsor || company.hiresInternationalStudents) && (
        <ul className="company-card-flags" aria-label="Visa support">
          {company.accreditedSponsor && (
            <li className="flag flag-sponsor">Visa sponsorship available</li>
          )}
          {company.hiresInternationalStudents && (
            <li className="flag flag-sponsor">Hires international students and graduates</li>
          )}
        </ul>
      )}

      <div className="company-card-foot">
        {industries.length > 0 && (
          <ul
            className="company-card-tags"
            aria-label="Industries"
            title={company.industries.join(', ')}
          >
            {industries.map((industry) => (
              <li key={industry} className="company-tag">
                {industry}
              </li>
            ))}
            {extraIndustries > 0 && (
              <li className="company-tag company-tag-more">+{extraIndustries}</li>
            )}
          </ul>
        )}

        <p className="company-card-meta">
          <span>{[meta, company.types.slice(0, 2).join(' · ')].filter(Boolean).join(' · ')}</span>
          {company.linkedin && (
            <a
              className="company-linkedin"
              href={outboundHref(company.linkedin, 'startups')}
              target="_blank"
              rel="noopener"
              referrerPolicy="strict-origin-when-cross-origin"
            >
              LinkedIn
            </a>
          )}
        </p>
      </div>
    </li>
  );
}
